import { lazy, Suspense } from 'react'

import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { CardCustomizationBlocker } from '@cash-web/protos/squareup/franklin/blockers/card_customization_blocker.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useShowCardStudio } from '@cash-web/shared-data-access-feature-flags'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { PushNotificationModal } from '@cash-web/shared-feature-common'
import { post } from '@cash-web/shared-util-fetch'
import { WebInteractClick } from '@squareup/customer-data-events-web'

const CardStudio = lazy(() =>
  import('@cash-web/account-card-studio-feature-main').then(module => ({ default: module.CardStudio }))
)

const getPushNotificationDetails = (t: TFunction, hasCashAppCard: boolean, page: string) => {
  if (page === '/money') {
    return {
      campaignId: 'CARD_DRAWER',
      notificationHeader: t('Get your free card', {
        context: 'CTA text for the button that starts the Cash App Card order process',
      }),
      notificationBody: t('Continue in the app to order a Cash App Card and start banking.', {
        context: 'CTA text for the button that starts the Cash App Card order process',
      }),
      subtitle: t(
        'To start banking with Cash App, order your Cash App Card in the app. We sent you a notification to get started.',
        { context: 'description for the Cash App Card promo' }
      ),
    }
  }

  return {
    campaignId: hasCashAppCard ? 'REPLACE_CARD' : 'NEW_CARD',
    notificationHeader: hasCashAppCard
      ? t('Design a new card', { context: 'title for an app notification to start ordering a new debit card' })
      : t('Get your free card', { context: 'title for an app notification to start ordering a new debit card' }),
    notificationBody: hasCashAppCard
      ? t('Continue in the app to design your new Cash App Card.', {
          context: 'body text for an app notification to start designing a new debit card',
        })
      : t('Continue in the app to order a Cash App Card.', {
          context: 'body text for an app notification to start ordering a new debit card',
        }),
    subtitle: hasCashAppCard
      ? t('To design a new card, open Cash App on your phone.', { context: 'Ordering a new debit card' })
      : t('Order your Cash App Card in the app. We sent you a notification to get started.', {
          context: 'Ordering a new debit card',
        }),
  }
}

export const BlockerCardCustomization = ({ blocker }: { blocker: CardCustomizationBlocker }) => {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const showCardStudio = useShowCardStudio()
  const { pathname } = useLocation()
  const { profile } = useAuth()

  const hasCashAppCard = Boolean(profile?.issued_card) && !profile?.issued_card?.virtual

  const { notificationHeader, notificationBody, subtitle, campaignId } = getPushNotificationDetails(
    t,
    hasCashAppCard,
    pathname
  )

  function sendNotification() {
    void logCdfEvent(
      WebInteractClick({
        pageName: 'card_customization blocker',
        componentName: 'Send notification again: design card',
      })
    )
    void post('/2.0/cash/web/campaign', { id: campaignId })
  }

  return showCardStudio ? (
    <Suspense fallback={null}>
      <CardStudio />
    </Suspense>
  ) : (
    <PushNotificationModal
      sendNotification={sendNotification}
      subtitle={subtitle}
      notificationHeader={notificationHeader}
      notificationBody={notificationBody}
    />
  )
}
